import {
  Button,
  Combobox,
  ErrorContainer,
  Form,
  FormControl,
  FormField,
  FormInput,
  FormItem,
  FormMessage,
  Heading,
  IconBin,
  IconInfo,
  IconPlus,
  ScrollArea,
} from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import RebrandButtonLink from "@/components/RebrandButtonLink";
import useSetlistsCreateUpdateTour from "@/components/tour/SetlistsCreateUpdateTour";
import Loading from "@/layouts/Loading";

import { useSetlistCreateUpdateForm } from "./hooks/useSetlistCreateUpdateForm";
import { useSetlistCreateUpdateMutation } from "./hooks/useSetlistCreateUpdateMutation";
import { str } from "./utils/setlistCreateUpdateUtils";

function SetlistsCreateUpdate() {
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfigAuth();
  useEffect(() => {
    if (siteConfig?.disable_gigs) {
      void navigate({ to: "/" });
    }
  }, [siteConfig?.disable_gigs, navigate]);

  const flags = useFlags();
  const showTourFlag = flags["pd-show-tutorial"] as boolean;

  const { t } = useTranslation();

  const startTour = useSetlistsCreateUpdateTour();

  const {
    form,
    id,
    isUpdate,
    selectedAccount,
    myAccount,
    profile,
    setlist,
    setlists,
    pending,
    setPending,
    mutationErrorMessage,
    setMutationErrorMessage,
    options,
    cachedSongs,
    handleInputChange,
  } = useSetlistCreateUpdateForm();

  const { addSetlist, updateSetlist } = useSetlistCreateUpdateMutation({
    id,
    form,
    cachedSongs,
    selectedAccount,
    myAccount,
    profile,
    setlist,
    setlists,
    t,
    setPending,
    setMutationErrorMessage,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "coverSongs",
  });

  return (
    <div className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      <div className="flex items-baseline gap-5 mb-6">
        {/* Header */}
        {isUpdate
          ? <Heading as="h3">{t("gigs:update-setlist-header")}</Heading>
          : <Heading as="h3">{t("gigs:create-setlist-header")}</Heading>}
        {showTourFlag && (
          <RebrandButtonLink onClick={startTour}>
            <div className="flex items-center gap-2 pb-1">
              {t("tutorial:need-help", "Need Help")}
              {" "}
              <IconInfo className="w-5 h-5 mb-1 stroke-action-primary" />
            </div>
          </RebrandButtonLink>
        )}
      </div>

      {id != null && setlist.isLoading && <Loading />}

      {(id == null || setlist.isSuccess) && (
        <Form {...form}>
          <form onSubmit={(e) => { void form.handleSubmit(isUpdate ? updateSetlist : addSetlist)(e); }}>
            <div className="bg-white shadow-sm rounded-lg p-4 md:px-14 md:py-10">
              <div className="flex flex-col gap-6 w-full">
                {/* Name */}
                <div id="setlist-name-section" className="flex flex-col gap-2">
                  <Heading as="h4">{t("gigs:setlist-name")}</Heading>
                  <div>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FormInput
                              id="txtSetlistName"
                              placeholder={str(t("gigs:setlist-name"))}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div id="setlist-songs-section" className="flex flex-col gap-2">
                  <Heading as="h4">{t("gigs:add-songs-to-setlist")}</Heading>
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={form.control}
                      name="songList"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Combobox
                              customWidth
                              triggerClassName="w-full max-w-none"
                              multiSelectDisplay="count"
                              placeholder={str(t("gigs:select-song"))}
                              values={field.value.map(s => s.workcode)}
                              onValuesChange={(workcodes) => {
                                field.onChange(
                                  workcodes.map((wc) => {
                                    const existing = field.value.find(s => s.workcode === wc);
                                    if (existing)
                                      return existing;
                                    const cached = cachedSongs.find(s => s.workcode === wc);
                                    const title = cached?.title ?? "";
                                    const artist = cached?.performingartists ?? "";
                                    return {
                                      workcode: wc,
                                      title,
                                      performingartists: artist,
                                      label: title
                                        ? artist ? `${title} - (${artist})` : title
                                        : wc,
                                    };
                                  }),
                                );
                              }}
                              data={options}
                              onSearchChange={handleInputChange}
                            />
                          </FormControl>
                          {field.value.length > 0 && (
                            <ScrollArea className="mt-2 h-48 rounded-lg border border-stroke-subtle">
                              <div className="flex flex-col gap-2 p-1">
                                {field.value.map(song => (
                                  <div
                                    key={song.workcode}
                                    className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm"
                                  >
                                    <span className="font-semibold">{song.label ?? song.title}</span>
                                    <Button
                                      className="self-center"
                                      type="button"
                                      variant="link"
                                      aria-label={str(t("gigs:remove-cover-song-row", "Remove cover song row"))}
                                      icon={<IconBin className="w-5 h-5 stroke-ink-secondary hover:stroke-ink-error" />}
                                      onClick={() => {
                                        field.onChange(
                                          field.value.filter(s => s.workcode !== song.workcode),
                                        );
                                      }}

                                    />
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div id="setlist-other-songs-section" className="flex flex-col gap-2">
                  <Heading as="h4">{t("gigs:do-you-cover-any-songs")}</Heading>
                  <div className="flex flex-col gap-2">
                    {fields.map((field, index) => (
                      <div key={field.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start">
                        <FormField
                          control={form.control}
                          name={`coverSongs.${index}.artist`}
                          render={({ field: f }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <FormInput placeholder={str(t("gigs:artist-placeholder"))} {...f} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`coverSongs.${index}.song`}
                          render={({ field: f }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <FormInput placeholder={str(t("gigs:songs-placeholder"))} {...f} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`coverSongs.${index}.tunecode`}
                          render={({ field: f }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <FormInput placeholder={str(t("gigs:tunecode-placeholder"))} {...f} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => remove(index)}
                          className="w-auto h-auto p-1"
                          aria-label={str(t("gigs:remove-cover-song-row", "Remove cover song row"))}
                          icon={<IconBin size="lg" className="stroke-ink-secondary hover:stroke-ink-error" />}
                        />
                      </div>
                    ))}
                    <div className="flex justify-end mt-2">
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => append({ artist: "", song: "", tunecode: "" })}
                        icon={<IconPlus className="w-5 h-5 stroke-ink-primary" />}
                        label={str(t("gigs:add-another"))}
                      />
                    </div>
                  </div>
                </div>

                <div id="setlist-notes-section" className="flex flex-col gap-2">
                  <Heading as="h4">{t("gigs:notes-header")}</Heading>
                  <div className="">
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <textarea
                              id="txtSetlistNotes"
                              placeholder={str(t("gigs:notes-placeholder"))}
                              rows={4}
                              className="w-full font-sentric-alt font-medium text-sm rounded-lg px-2 py-1.5 ring-1 ring-canvas-inverse hover:ring-2 hover:ring-stroke-primary focus-visible:ring-2 focus-visible:ring-stroke-primary focus-visible:outline-none placeholder:text-black/50 transition ease-out duration-200 resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {mutationErrorMessage.length > 0 && (
                    <ErrorContainer message={mutationErrorMessage} />
                  )}
                  {form.formState.errors.root?.message != null && (
                    <ErrorContainer message={form.formState.errors.root.message} />
                  )}
                  <div className="flex justify-center sm:justify-end">
                    <Button
                      type="submit"
                      disabled={pending}
                      label={isUpdate
                        ? str(t("gigs:update-setlist-button"))
                        : str(t("gigs:add-setlist-button"))}
                      className="justify-center"
                      id={isUpdate ? "btnSetlistUpdate" : "btnSetlistAdd"}
                      variant="primary"
                    />
                  </div>
                </div>

              </div>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}

export default SetlistsCreateUpdate;
